import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { DrawerInputConfig } from '../../../models/drawer-model';
import { RouteService } from '../../../service/route.service';
import { API_ENDPOINTS } from '../../shared/constants/apiEnpoints';
import { DrawerComponent } from '../../shared/drawer/drawer.component';
import { DynamicGridComponent } from '../../shared/dynamic-grid/dynamic-grid.component';
import { GridConfig } from '../../shared/dynamic-grid/grid-model/grid-config.model';
import { Toast, ToastComponent } from '../../shared/toast/toast.component';
import { HttpClient } from '@angular/common/http';

export interface PracticeOperationalHoursData {
  id?: string;
  practiceId: string;
  practiceCode?: string;
  practiceName: string;
  pmsName?: string;
  day: number;
  dayName?: string;
  openingTime: string;
  closingTime: string;
  isClosed: boolean;
}

@Component({
  selector: 'app-practice-operational-hours',
  templateUrl: './practice-operational-hours.component.html',
  styleUrl: './practice-operational-hours.component.scss',
})
export class PracticeOperationalHoursComponent {
  @ViewChild('operationalHoursDrawer') drawerComponent!: DrawerComponent<PracticeOperationalHoursData>;
  @ViewChild(DynamicGridComponent) dynamicGrid!: DynamicGridComponent;

  gridConfig: GridConfig = {
    columns: [
      { dataType: 'string', jsonKey: 'practiceCode', label: 'Practice Code' },
      { dataType: 'string', jsonKey: 'practiceName', label: 'Practice Name', width: 'full' },
      { dataType: 'string', jsonKey: 'dayName', label: 'Day' },
      { dataType: 'string', jsonKey: 'openingTime', label: 'Opening Time' },
      { dataType: 'string', jsonKey: 'closingTime', label: 'Closing Time' },
      { dataType: 'boolean', jsonKey: 'isClosed', label: 'Closed' },
    ],
    filters: [
      {
        label: 'Practice',
        jsonKey: 'practiceId',
        type: 'multi',
        options: [],
        width: 'w-50',
      },
    ],
    deletable: false,
    editable: true,
    exportable: true,
    displayAdd: true,
    isToggleDisabled: false,
    defaultFilters: {},
    uniqueTableName: 'PracticeOperationalHours',
    displayHideAuditColumnsToggle: true,
  };

  getDataUrl = API_ENDPOINTS.PRACTICE_OPERATIONAL_HOURS;
  addDataUrl = API_ENDPOINTS.PRACTICE_OPERATIONAL_HOURS;
  editDataUrl = API_ENDPOINTS.PRACTICE_OPERATIONAL_HOURS_BY_ID;
  toggleUrl = API_ENDPOINTS.TOGGLE_PRACTICE_OPERATIONAL_HOURS;
  getPracticesUrl = API_ENDPOINTS.GETALLACTIVEPRACTICES;

  drawerInputConfig: DrawerInputConfig[] = [
    {
      dataType: 'string',
      jsonKey: 'practiceName',
      label: 'Practice',
      input: 'select',
      uniqueIdentifier: 'PracticeOperationalHoursPractice',
      dropDownOptionsURL: API_ENDPOINTS.GETALLACTIVEPRACTICES,
      dropdownJsonKey: 'name',
      isRequired: true,
      dropdownSortDirection: 'asc',
      foreignKeyProperty: 'practiceId',
      isDropDownSearchable: true
    },
    {
      dataType: 'number',
      jsonKey: 'day',
      label: 'Day of Week',
      input: 'select',
      isRequired: true,
      uniqueIdentifier: 'DayOfWeek',
      options: [
        { id: 1, name: 'Monday' },
        { id: 2, name: 'Tuesday' },
        { id: 3, name: 'Wednesday' },
        { id: 4, name: 'Thursday' },
        { id: 5, name: 'Friday' },
        { id: 6, name: 'Saturday' },
        { id: 7, name: 'Sunday' },
      ],
      dropdownJsonKey: 'name'
    },
    {
      dataType: 'time',
      jsonKey: 'openingTime',
      label: 'Opening Time',
      input: 'time',
      isRequired: false
    },
    {
      dataType: 'time',
      jsonKey: 'closingTime',
      label: 'Closing Time',
      input: 'time',
      isRequired: false
    },
    {
      label: 'Closed',
      jsonKey: 'isClosed',
      input: 'toggle',
      dataType: 'boolean',
      fieldsToDisableOnToggle: ['openingTime', 'closingTime']
    }
  ];

  drawerModel: PracticeOperationalHoursData = {
    practiceId: '',
    practiceName: 'Select Practice',
    day: 7,
    dayName: 'Sunday',
    openingTime: '',
    closingTime: '',
    isClosed: false
  };

  private _routerSubscription: Subscription;
  routes: string[] = [];

  constructor(
    private routeService: RouteService,
    private cdr: ChangeDetectorRef,
    private _http: HttpClient
  ) { }

  ngOnInit() {
    this.fetchPracticeFilterOptions();
  }

  ngAfterViewInit(): void {    this.drawerComponent.drawerModelName = 'practiceOperationalHoursData';
    this._routerSubscription = this.routeService
      .getRoutes()
      .subscribe((data) => {
        this.routes = data;
        this.routes.shift();
        this.cdr.detectChanges();
      });
  }

  onAddHandler(): void {
    if (this.drawerComponent) {
      this.drawerComponent.drawerType = 'add';
      this.drawerInputConfig.find(c => c.jsonKey === 'practiceName').isDisabled = false;
      this.drawerInputConfig.find(c => c.jsonKey === 'day').isDisabled = false;
      this.resetEntry();
      this.drawerComponent.openDrawer();
    }
  }

  resetEntry() {
    this.drawerModel = {
      practiceId: '',
      practiceName: 'Select Practice',
      day: 7,
      dayName: 'Sunday',
      openingTime: '',
      closingTime: '',
      isClosed: false
    };
  }

  getDayName(day: number): string {
    const days = { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday', 7: 'Sunday' };
    return days[day] || 'Unknown';
  }

  onEditHandler(rowData: PracticeOperationalHoursData): void {
    if (this.drawerComponent) {
      this.drawerComponent.drawerType = 'edit';
      const formattedPracticeLabel = rowData.practiceCode
        ? `${rowData.practiceCode} (${rowData.pmsName || rowData.practiceName})`
        : rowData.practiceName;
      const editModel = {
        ...rowData,
        practiceName: formattedPracticeLabel,
      };
      // Convert day number to proper dropdown format for display, but keep original day value
      const dayOption = this.drawerInputConfig.find(c => c.jsonKey === 'day')?.options?.find(opt => opt.id === rowData.day);
      if (dayOption) {
        // Set the dropdown display value but keep the original numeric day
        editModel.day = { id: dayOption.id, name: dayOption.name } as any;
      }
      this.drawerComponent.drawerModel = editModel;
      this.drawerInputConfig.find(c => c.jsonKey === 'practiceName').isDisabled = true;
      this.drawerInputConfig.find(c => c.jsonKey === 'day').isDisabled = false;
      this.drawerComponent.openDrawer();
    }
  }



  refreshHandler() {
    this.dynamicGrid.fetchData();
  }

  fetchPracticeFilterOptions() {
    this._http.get<any>(this.getPracticesUrl).subscribe((response) => {
      response.data = response.data
        .map((option) => ({ ...option, name: option.practiceCode }))
        .sort((a, b) => a.name.localeCompare(b.name));
      response.data = [{ id: 'All', name: 'All Practices', checked: true }, ...response.data];
      this.gridConfig.filters.find((f) => f.jsonKey === 'practiceId').options = response;
    });
  }

  @ViewChild(ToastComponent) toastComponent!: ToastComponent;

  showToast(toast: Toast) {
    this.toastComponent.showToast(toast);
    this.refreshHandler();
  }

  ngOnDestroy() {
    this._routerSubscription?.unsubscribe();
  }
}
